import { useState, useCallback, useRef, useEffect } from 'react';

const CANVAS_SIZE = 1000;

export const usePanZoom = (containerSize = { w: window.innerWidth, h: window.innerHeight }) => {
  const getInitialTransform = () => {
    const isMobile = window.innerWidth < 768;
    const scale = isMobile ? 0.8 : 1.5;
    const x = (containerSize.w - CANVAS_SIZE * scale) / 2;
    const y = (containerSize.h - CANVAS_SIZE * scale) / 2;
    return { x, y, scale };
  };

  const [transform, setTransform] = useState(getInitialTransform);
  const isDragging = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setTransform(getInitialTransform());
  }, [containerSize.w, containerSize.h]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = e.currentTarget?.getBoundingClientRect?.() || { left: 0, top: 0 };
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    setTransform((prev) => {
      const zoomFactor = e.deltaY > 0 ? 0.92 : 1.08;
      const newScale = Math.min(Math.max(0.3, prev.scale * zoomFactor), 10);
      const scaleRatio = newScale / prev.scale;
      return {
        scale: newScale,
        x: mouseX - scaleRatio * (mouseX - prev.x),
        y: mouseY - scaleRatio * (mouseY - prev.y),
      };
    });
  }, []);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastMousePos.current.x;
    const dy = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    setTransform((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const flyToTile = useCallback((tile, size) => {
    const targetScale = 4;
    const centerX = size.w / 2 - (tile.x + tile.w / 2) * targetScale;
    const centerY = size.h / 2 - (tile.y + tile.h / 2) * targetScale;
    setTransform({ x: centerX, y: centerY, scale: targetScale });
  }, []);

  return { transform, setTransform, handleWheel, handleMouseDown, handleMouseMove, handleMouseUp, flyToTile };
};